import { useEffect, useState } from 'react';
import { Link, OPINION, PLATFORM_ORDER, RUNNING, STATUS_LABEL, ago, fmtCredits, nf, opinions, pct, targetsTitle, useApi } from '../lib.jsx';
import { CreditMeter, Icon, PlatformDot, StatCard, Status } from '../ui.jsx';
import { Seg } from './NewResearch.jsx';

/** A thin sentiment bar for one target, from the saved report: shares of the comments that give a view. */
function MiniSentiment({ s }) {
  const op = opinions(s.analysed, s);
  if (!op) return null;
  return (
    <span className="mini-sent" title={OPINION.map((x) => `${x.label} ${pct(s[x.key] / op)}`).join(' · ')}>
      <span className="mini-bar">{OPINION.filter((x) => s[x.key]).map((x) => <i key={x.key} style={{ flex: s[x.key], background: x.color }} />)}</span>
      <span className="small muted">{s.name}: {pct(s.positive / op)} positive · {pct(s.negative / op)} negative</span>
    </span>
  );
}

/** The workspace's research, read again every few seconds while any of it is running. */
function useRuns() {
  const api = useApi('/api/runs');
  const runs = api.data?.runs ?? [];
  const anyRunning = runs.some((r) => RUNNING.includes(r.status));
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(api.reload, 4000);
    return () => clearInterval(t);
  }, [anyRunning, api.reload]);
  return { ...api, runs };
}

const isRunning = (r) => RUNNING.includes(r.status);
/** A finished scan: its posts are the result, so it is ready without a report. */
const scanDone = (r) => r.preset === 'scan' && ['complete', 'partial'].includes(r.status);
const platformsOf = (r) => PLATFORM_ORDER.filter((p) => r.platforms.includes(p));
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

function CreditsStat({ c }) {
  if (c.team) return (
    <StatCard label="Credits used" value={fmtCredits(c.used)}>
      <span className="foot">Team account: research is not limited by credits.</span>
    </StatCard>
  );
  return (
    <StatCard label="Credits remaining" value={fmtCredits(c.left)} unit={`of ${fmtCredits(c.total)}`}>
      <CreditMeter used={c.used} total={c.total} />
      <span className="foot"><span>{fmtCredits(c.used)} used</span><Link to="/account">Usage and history</Link></span>
    </StatCard>
  );
}

/** Research still running: where it has got to. */
function ProgressCard({ r }) {
  const step = RUNNING.indexOf(r.status);
  return (
    <Link className="progress-card" to={`/runs/${r.id}`}>
      <span className="spinner" aria-hidden="true" />
      <span className="pc-main">
        <b>{targetsTitle(r.targets)}</b>
        <span className="small muted">{STATUS_LABEL[r.status] ?? 'Working'} · started {ago(r.createdAt)}</span>
        <span className="progress" role="img" aria-label={`Step ${step + 1} of ${RUNNING.length}`}><i style={{ width: `${((step + 1) / (RUNNING.length + 1)) * 100}%` }} /></span>
      </span>
      <Icon name="arrow" />
    </Link>
  );
}

/** Where a piece of research stands, in a word or two. */
function RunBadge({ r }) {
  if (r.report) return <span className={`badge ready${r.status === 'partial' ? ' partial' : ''}`}>{r.status === 'partial' ? 'Report ready · partial' : 'Report ready'}</span>;
  if (scanDone(r)) return <span className={`badge ready${r.status === 'partial' ? ' partial' : ''}`}>{r.status === 'partial' ? 'Scan ready · partial' : 'Scan ready'}</span>;
  if (isRunning(r)) return <Status status={r.status} />;
  if (['complete', 'partial'].includes(r.status)) return <span className="badge" title={r.noReport ? 'The analysis did not run; open it to see why' : 'Open it and choose Analyse to make a report'}>Collected · not analysed</span>;
  return <Status status={r.status} />;
}

/** A piece of research as a card: what it is about, its first finding and how people feel, or where it stands. */
function ReportCard({ r }) {
  return (
    <Link className="report-card" to={`/runs/${r.id}`}>
      <span className="rc-platforms">{platformsOf(r).map((p) => <PlatformDot key={p} p={p} />)}</span>
      <h3>{targetsTitle(r.targets)}</h3>
      <p className="rc-headline">{r.report?.headline ?? r.question}</p>
      {r.report ? <span className="rc-sent">{r.report.sentiment.slice(0, 2).map((s) => <MiniSentiment key={s.name} s={s} />)}</span> : <span className="rc-status"><RunBadge r={r} /></span>}
      <span className="rc-foot">
        <span>{nf.format(r.comments)} comments</span>
        <span>{ago(r.createdAt)}</span>
        {r.synthetic ? <span className="tag">demo data</span> : null}
      </span>
    </Link>
  );
}

function GetStarted() {
  return (
    <section className="get-started">
      <div className="gs-text">
        <p className="eyebrow">Get started</p>
        <h2>Your first report is a few minutes <em>away</em>.</h2>
        <p className="muted">Yapscape reads public TikTok, Instagram, Reddit, YouTube and LinkedIn posts and the comments under them, and Amazon customer reviews, then tells you what people praise, criticise and ask about. Every finding links back to the original comments.</p>
        <div className="row-actions"><Link className="btn primary" to="/new">Start research</Link><Link className="btn" to="/sample">See a sample report</Link></div>
      </div>
      <ol className="how">
        <li><span><b>Describe it.</b> A brand, a product, or two to compare.</span></li>
        <li><span><b>We collect and read.</b> Posts and comments from the dates and market you choose.</span></li>
        <li><span><b>Get a report.</b> Summary, actions to test, themes and every comment behind them.</span></li>
      </ol>
    </section>
  );
}

export function Dashboard({ user, credits }) {
  const { runs, data, error } = useRuns();
  const ready = runs.filter((r) => r.report);
  const running = runs.filter(isRunning);
  const first = (user.name ?? '').split(' ')[0];
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{greeting()}</p>
          <h1>{first ? <>Welcome back, <em>{first}</em>.</> : <>Welcome to <em>{user.workspace.name}</em>.</>}</h1>
          <p className="muted">{user.workspace.name} on Yapscape · reading a report never uses credits</p>
        </div>
        {runs.length ? <Link className="btn primary page-cta" to="/new"><Icon name="plus" />New research</Link> : null}
      </div>
      {error ? <div className="error-box">{error.message}</div> : null}
      <div className="stat-row">
        {credits ? <CreditsStat c={credits} /> : null}
        <StatCard label="Reports ready" value={data ? nf.format(ready.length) : '–'}>
          <span className="foot">{running.length ? `${running.length} in progress` : runs.length ? `${nf.format(runs.length)} in total` : 'None yet'}</span>
        </StatCard>
        <StatCard label="Comments read" value={data ? nf.format(runs.reduce((a, r) => a + r.comments, 0)) : '–'}>
          <span className="foot">from {nf.format(runs.reduce((a, r) => a + r.posts, 0))} posts</span>
        </StatCard>
      </div>
      {!data && !error ? <div className="skeleton-list" aria-hidden="true"><i /><i /></div> : null}
      {data && !runs.length ? <GetStarted /> : null}
      {running.length ? (
        <section className="dash-section">
          <div className="section-head"><h2>In progress</h2><span className="small muted">You can leave; the report will be here when it is ready.</span></div>
          <div className="progress-list">{running.map((r) => <ProgressCard key={r.id} r={r} />)}</div>
        </section>
      ) : null}
      {runs.length && !ready.length ? (
        <div className="dash-note"><p>No finished report yet. The sample shows what you get when one is ready.</p><Link className="btn small" to="/sample">Open the sample report</Link></div>
      ) : null}
      {runs.length > running.length ? (
        <section className="dash-section">
          <div className="section-head"><h2>Recent research</h2><Link to="/reports" className="small">All reports →</Link></div>
          <div className="report-grid">{runs.filter((r) => !isRunning(r)).slice(0, 6).map((r) => <ReportCard key={r.id} r={r} />)}</div>
        </section>
      ) : null}
    </div>
  );
}

const FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'ready', label: 'Ready', test: (r) => !!r.report || scanDone(r) },
  { id: 'running', label: 'In progress', test: isRunning },
  { id: 'other', label: 'Not finished', test: (r) => !r.report && !scanDone(r) && !isRunning(r) },
];

export function Reports() {
  const { runs, data, error } = useRuns();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const words = q.trim().toLowerCase();
  const shown = runs.filter(FILTERS.find((f) => f.id === filter).test)
    .filter((r) => !words || `${r.targets.join(' ')} ${r.question} ${r.report?.headline ?? ''}`.toLowerCase().includes(words));
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>All <em>reports</em></h1>
          {runs.length ? <p className="muted">{nf.format(runs.length)} {runs.length === 1 ? 'study' : 'studies'} · reports open from saved data, so reading them never uses credits</p> : null}
        </div>
        <Link className="btn primary page-cta" to="/new"><Icon name="plus" />New research</Link>
      </div>
      {error ? <div className="error-box">{error.message}</div> : null}
      {!data && !error ? <div className="skeleton-list" aria-hidden="true"><i /><i /><i /></div> : null}
      {data && !runs.length ? <GetStarted /> : null}
      {runs.length ? <>
        <div className="list-tools">
          <input type="search" placeholder="Search reports" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search reports" />
          <Seg value={filter} onChange={setFilter} label="Show" options={FILTERS.map((f) => ({ id: f.id, label: `${f.label} ${runs.filter(f.test).length}` }))} />
        </div>
        {shown.length ? (
          <ul className="run-list">
            {shown.map((r) => (
              <li className="run-row" key={r.id}>
                <Link to={`/runs/${r.id}`}>
                  <span className="run-q">{targetsTitle(r.targets)}</span>
                  <span className="run-headline">{r.report?.headline ?? r.question}</span>
                  {r.report ? <span className="run-sent">{r.report.sentiment.map((s) => <MiniSentiment key={s.name} s={s} />)}</span> : null}
                  <span className="run-meta">
                    {platformsOf(r).map((p) => <PlatformDot key={p} p={p} />)}
                    <span>{nf.format(r.posts)} posts, {nf.format(r.comments)} comments</span>
                    {r.credits ? <span>{fmtCredits(r.credits)} credits</span> : null}
                    {r.synthetic ? <span className="tag">demo data</span> : null}
                    <span>{ago(r.createdAt)}</span>
                  </span>
                  <span className="run-side">
                    <RunBadge r={r} />
                    {r.report && r.status === 'partial' ? <span className="small muted">{STATUS_LABEL.partial}</span> : null}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : <p className="muted empty-note">No report matches{words ? ` “${q.trim()}”` : ''}.</p>}
      </> : null}
    </div>
  );
}
